import { AppConfig } from '@superbet-group/web.lib.config';
import { loadScript } from '@superbet-group/web.lib.utilities';

import { logger } from '@app/logger';
import RestService from '@core/services/antiFraud/_private/RestService';
import { sessionStorageService } from '@core/services/antiFraud/_private/SessionStorageService';
import {
    SeonSession,
    Transaction,
    Withdrawal,
    Deposit,
    SessionStorageService,
    SeonService,
} from '@core/services/antiFraud/types';
import User from '@models/user/User';
import { TransactionType } from '@superbet-group/web.types';
import generateNumberFromUuid from './helpers';
import {
    AntifraudErrorLocation,
    logAntifraudError,
} from './debug';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';

class AntiFraudService {
    private restService: RestService;
    private seonService: SeonService;
    private sessionStorageService: SessionStorageService;
    private userId: number | BigInt;
    private initialized = false;
    private requestQueue: Transaction[] = [];
    private isEnabled = false;
    private isSeonForComtradeEnabled = false;

    constructor(sessionStorageService: SessionStorageService) {
        this.sessionStorageService = sessionStorageService;
    }

    public sendDeposit(deposit: Deposit): Promise<void> {
        return this.sendTransaction({
            ...deposit,
            type: TransactionType.Deposit,
        });
    }

    public sendWithdrawal(withdrawal: Withdrawal): Promise<void> {
        return this.sendTransaction({
            ...withdrawal,
            type: TransactionType.Withdrawal,
        });
    }

    public sendTransaction(data: Transaction): Promise<void> {
        if (!this.isEnabled) {
            return Promise.resolve();
        }

        if (this.initialized) {
            return this._sendTransaction(data);
        } else {
            this.requestQueue.push(data);
            return Promise.resolve();
        }
    }

    public isComtradeSeonFlow(): boolean {
        return this.isSeonForComtradeEnabled;
    }

    public async initialize(config: AppConfig['antiFraud'], userId?: User['userId']): Promise<void> {
        if (this.userId !== userId) {
            this.cleanup();
        }

        this.userId = userId || generateNumberFromUuid();

        if (!this.isEnabled) {
            return;
        }

        if (this.initialized) {
            await this.updateSession();
            return;
        }

        if (shellLocalConfig.seonConfig.isComtradeFlowSupported) {
            this.subscribeToSeonComtradeStatus();
        }

        this.restService = new RestService(config);

        await this.loadSeonScript();
        this.seonService = window.seon;

        try {
            await this.updateSession();
            this.initialized = true;

            if (this.requestQueue.length > 0) {
                this.requestQueue.forEach((data) => this._sendTransaction(data));
                this.requestQueue = [];
            }
        } catch (e) {
            logAntifraudError(AntifraudErrorLocation.Initialize, e as Error);
            logger.error(e as Error);
        }
    }

    private subscribeToSeonComtradeStatus(): void {
        featureFlagService.subscribeToFeatureChanges(AccountFlagNames.isSeonForComtradeEnabled, (isEnabled: boolean) => {
            this.isSeonForComtradeEnabled = isEnabled;
        }, false);
    }

    // eslint-disable-next-line class-methods-use-this
    private async loadSeonScript(): Promise<void> {
        try {
            await loadScript('https://cdn.seondf.com/js/v6/agent.umd.js', 'seon');
        } catch (e) {
            logAntifraudError(AntifraudErrorLocation.LoadSeonScript, e as Error);
        }
    }

    public cleanup(): void {
        this.sessionStorageService.clear();
    }

    private async _sendTransaction({ method, type, transactionData }: Transaction): Promise<void> {
        try {
            const seonSession: SeonSession = await this.getSession();
            await this.restService.sendTransaction(method, type, transactionData, seonSession);
        } catch (e) {
            logAntifraudError(AntifraudErrorLocation.SendTransaction, e as Error);
            logger.error(e as Error);
        }
    }

    public renewSession(): Promise<void> {
        return this.generateNewSession();
    }

    public getCurrentSession() {
        return {
            session: this.sessionStorageService.get(),
            sessionId: this.sessionStorageService.getId(),
        };
    }

    private async getSession(): Promise<SeonSession> {
        await this.updateSession();
        return this.sessionStorageService.get();
    }

    private updateSession(): Promise<void> | void {
        if (!this.sessionStorageService.exists()) {
            return this.generateNewSession();
        }
    }

    private generateNewSession(): Promise<void> {
        if (!this.isEnabled) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            try {
                const sessionId = `${this.userId}${Date.now()}`;
                const config = {
                    dnsResolverDomain: 'seondfresolver.com',
                };

                this.seonService.getSession(config).then((session: SeonSession) => {
                    if (session) {
                        this.sessionStorageService.set(session);
                        this.sessionStorageService.setId(sessionId);
                        return resolve();
                    } else {
                        logAntifraudError(AntifraudErrorLocation.GetSession, 'unknown_success_but_error');
                        throw new Error('Failed to get SEON session');
                    }
                }).catch((error) => {
                    logAntifraudError(AntifraudErrorLocation.GetSession, error as Error);
                    reject(error);
                });
            } catch (e) {
                reject(e);
            }
        });
    }

    public setEnabled(isEnabled: boolean): void {
        this.isEnabled = isEnabled;
    }
}

export const antiFraudService = new AntiFraudService(sessionStorageService);
